// ===========================================
// PIXI REGISTRY - POSITION CALCULATIONS
// ===========================================
// Shared positioning functions for kitty, cards,
// insurance, and outcome labels.
// ===========================================

import {
  KITTY_BASE_RADIAL_OFFSET,
  KITTY_PAIR_GAP,
  CARD_OVERLAP,
  SEAT_BASE_DIMENSIONS,
  DEFAULT_SCOREBOX_MIN_WIDTH,
  SCOREBOX_MIN_TEXT_WIDTH,
  SeatPosition,
  SeatConfig,
  ChipConfig,
  InsuranceChipConfig,
  DEFAULT_SEAT_CONFIG,
  DEFAULT_CHIP_CONFIG,
  DEFAULT_INSURANCE_CHIP_CONFIG,
} from './constants';
import { degreesToRadians, applyRadialOffset, applyTangentOffset, localToWorld } from './RotationUtils';

// ===========================================
// KITTY POSITION CALCULATIONS
// ===========================================

export interface KittyPositionConfig {
  betChipConfig?: ChipConfig;
  kittyOffsetX?: number;
  kittyOffsetY?: number;
}

export interface KittyPositionResult {
  x: number;
  y: number;
  rotationRad: number;
}

/**
 * Calculate the position for a main bet kitty display.
 *
 * @param seatData - Seat position data { x, y, rotation }
 * @param config - Optional configuration for offsets
 * @returns Position and rotation for the kitty container
 */
export function calculateKittyPosition(
  seatData: SeatPosition,
  config: KittyPositionConfig = {}
): KittyPositionResult {
  const betConfig = config.betChipConfig ?? DEFAULT_CHIP_CONFIG;
  const kittyOffsetX = config.kittyOffsetX ?? 0;
  const kittyOffsetY = config.kittyOffsetY ?? 0;

  // Calculate radial offset (distance from seat toward dealer)
  const radialOffset = KITTY_BASE_RADIAL_OFFSET + betConfig.radiusOffset;
  const rotationRad = degreesToRadians(seatData.rotation);

  // Apply radial offset with rotation
  const basePos = applyRadialOffset(seatData.x, seatData.y, radialOffset, rotationRad);

  // Apply additional config offsets
  return {
    x: basePos.x + betConfig.x + kittyOffsetX,
    y: basePos.y + betConfig.y + kittyOffsetY,
    rotationRad,
  };
}

/**
 * Calculate the position for an insurance kitty display.
 *
 * @param seatData - Seat position data
 * @param insuranceConfig - Insurance chip configuration
 * @param betChipConfig - Main bet chip configuration (for radial offset)
 * @param kittyOffsetX - Additional X offset from kitty config
 * @returns Position and rotation for the insurance kitty container
 */
export function calculateInsuranceKittyPosition(
  seatData: SeatPosition,
  insuranceConfig: InsuranceChipConfig = DEFAULT_INSURANCE_CHIP_CONFIG,
  betChipConfig: ChipConfig = DEFAULT_CHIP_CONFIG,
  kittyOffsetX: number = 0
): KittyPositionResult {
  const rotationRad = degreesToRadians(seatData.rotation);

  // Calculate base radial offset (same as main kitty)
  const radialOffset = KITTY_BASE_RADIAL_OFFSET + betChipConfig.radiusOffset;

  // Apply radial offset to get base position
  const basePos = applyRadialOffset(seatData.x, seatData.y, radialOffset, rotationRad);

  // Insurance kitty uses same Y (radial distance) as main kitty
  // Only tangent offset is applied later to shift horizontally
  return {
    x: basePos.x + kittyOffsetX,
    y: basePos.y,
    rotationRad,
  };
}

// ===========================================
// KITTY CENTERING (FOR PAIRED KITTIES)
// ===========================================

/**
 * Calculate the centering offset for a kitty when paired with another.
 * Used to center main + insurance kitty pairs.
 *
 * @param amount - Bet amount (used to estimate pill width)
 * @param padding - Gap between paired kitties
 * @param isMainKitty - True for main bet, false for insurance
 * @param hasPartner - Whether there's a partner kitty
 * @returns Tangent offset to apply
 */
export function calculateKittyCenteringOffset(
  amount: number,
  padding: number = KITTY_PAIR_GAP,
  isMainKitty: boolean = true,
  hasPartner: boolean = false
): number {
  if (!hasPartner) return 0;

  // Estimate pill width based on amount (rough approximation)
  const digits = Math.max(1, Math.floor(Math.log10(Math.abs(amount) + 1)) + 1);
  const estimatedPillWidth = 20 + digits * 8; // Base width + per-digit

  // Main kitty shifts left (negative), insurance shifts right (positive)
  const halfPill = estimatedPillWidth / 2;
  const halfPadding = padding / 2;

  return isMainKitty ? -(halfPill + halfPadding) : (halfPill + halfPadding);
}

/**
 * Apply kitty centering offset to a position.
 *
 * @param basePosition - Base kitty position
 * @param amount - Bet amount
 * @param isMainKitty - True for main bet, false for insurance
 * @param hasPartner - Whether there's a partner kitty
 * @param rotationRad - Rotation in radians
 * @returns Adjusted position with centering offset
 */
export function applyKittyCenteringOffset(
  basePosition: { x: number; y: number },
  amount: number,
  isMainKitty: boolean,
  hasPartner: boolean,
  rotationRad: number
): { x: number; y: number } {
  const offset = calculateKittyCenteringOffset(amount, KITTY_PAIR_GAP, isMainKitty, hasPartner);
  return applyTangentOffset(basePosition, offset, rotationRad);
}

// ===========================================
// CARD POSITION CALCULATIONS
// ===========================================

/**
 * Calculate the centered offset for a card in a hand.
 * Centers the hand horizontally around the seat position.
 *
 * @param cardIndex - Index of this card in the hand (0-based)
 * @param totalCards - Total number of cards in the hand
 * @param overlap - Overlap distance between cards (default: CARD_OVERLAP)
 * @returns Horizontal offset from center
 */
export function calculateCardCenteredOffset(
  cardIndex: number,
  totalCards: number,
  overlap: number = CARD_OVERLAP
): number {
  return (cardIndex - (totalCards - 1) / 2) * overlap;
}

/**
 * Calculate the world position for a player's card.
 *
 * @param seatPosition - Seat position data
 * @param cardIndex - Index of this card in the hand
 * @param totalCards - Total number of cards in the hand
 * @param cardsYOffset - Y offset for cards relative to seat (toward dealer)
 * @returns World coordinates for the card
 */
export function calculatePlayerCardPosition(
  seatPosition: SeatPosition,
  cardIndex: number,
  totalCards: number,
  cardsYOffset: number
): { x: number; y: number; rotationRad: number } {
  const rotationRad = degreesToRadians(seatPosition.rotation);
  const centeredOffset = calculateCardCenteredOffset(cardIndex, totalCards);

  const pos = localToWorld(
    seatPosition.x,
    seatPosition.y,
    centeredOffset,
    cardsYOffset,
    rotationRad
  );

  return {
    x: pos.x,
    y: pos.y,
    rotationRad,
  };
}

/**
 * Calculate the world position for a dealer's card.
 *
 * @param dealerX - Dealer X position
 * @param dealerY - Dealer Y position
 * @param cardIndex - Index of this card
 * @param totalCards - Total number of cards
 * @param cardsXOffset - X offset from dealer position
 * @param cardsYOffset - Y offset from dealer position
 * @param holeCardRevealed - Whether hole card has been revealed
 * @param scaledCardWidth - Width of card after scaling
 * @param scaledOverlap - Optional overlap distance scaled to card size
 * @returns World coordinates for the card
 */
export function calculateDealerCardPosition(
  dealerX: number,
  dealerY: number,
  cardIndex: number,
  totalCards: number,
  cardsXOffset: number,
  cardsYOffset: number,
  holeCardRevealed: boolean,
  scaledCardWidth: number,
  scaledOverlap?: number
): { x: number; y: number } {
  let cardX: number;

  if (!holeCardRevealed && totalCards <= 2) {
    // Before reveal: gap between cards (no overlap)
    const twoCardCenterOffset = (scaledCardWidth + 8) / 2;
    cardX = cardIndex === 0
      ? dealerX + cardsXOffset - twoCardCenterOffset
      : dealerX + cardsXOffset + twoCardCenterOffset;
  } else {
    // After reveal or for hits: centered overlap using scaled overlap if provided
    const centeredOffset = calculateCardCenteredOffset(cardIndex, totalCards, scaledOverlap);
    cardX = dealerX + cardsXOffset + centeredOffset;
  }

  return {
    x: cardX,
    y: dealerY + cardsYOffset,
  };
}

// ===========================================
// OUTCOME LABEL POSITION CALCULATIONS
// ===========================================

export interface OutcomeLabelPositionConfig {
  seatConfig?: SeatConfig;
  seatSizeScale?: number;
  scoreboxConfig?: {
    x: number;
    y: number;
    scale: number;
    fontSize: number;
    minWidth?: number;
  };
}

/**
 * Calculate seat dimensions based on config and scale.
 *
 * @param seatConfig - Per-seat configuration
 * @param seatSizeScale - Global seat size scale (percentage)
 * @returns Calculated width and height
 */
export function calculateSeatDimensions(
  seatConfig: SeatConfig = DEFAULT_SEAT_CONFIG,
  seatSizeScale: number = 100
): { width: number; height: number } {
  const seatScale = (seatSizeScale / 100) * (seatConfig.scale / 100);
  return {
    width: Math.round(SEAT_BASE_DIMENSIONS.width * seatScale),
    height: Math.round(SEAT_BASE_DIMENSIONS.height * seatScale),
  };
}

/**
 * Calculate the adjusted seat position including config offsets.
 *
 * @param basePosition - Base seat position
 * @param seatConfig - Per-seat configuration
 * @returns Adjusted position and rotation
 */
export function calculateAdjustedSeatPosition(
  basePosition: SeatPosition,
  seatConfig: SeatConfig = DEFAULT_SEAT_CONFIG
): { x: number; y: number; rotation: number } {
  return {
    x: basePosition.x + seatConfig.x,
    y: basePosition.y + seatConfig.y,
    rotation: basePosition.rotation + seatConfig.rot,
  };
}

/**
 * Calculate scorebox width for outcome label positioning.
 *
 * @param score - Current score to display
 * @param fontSize - Font size for text measurement
 * @param scale - Scorebox scale (percentage)
 * @param minWidth - Minimum width
 * @param measureTextWidth - Function to measure text width
 * @returns Calculated scorebox width
 */
export function calculateScoreboxWidth(
  score: number | undefined,
  fontSize: number,
  scale: number,
  minWidth: number = DEFAULT_SCOREBOX_MIN_WIDTH,
  measureTextWidth?: (text: string, fontSize: number) => number
): number {
  if (!score || !measureTextWidth) {
    return minWidth;
  }

  const padScore = (s: number) => s < 10 ? `0${s}` : s.toString();
  const displayText = padScore(score);
  const boxScale = scale / 100;

  const textWidth = measureTextWidth(displayText, fontSize);
  const calculatedWidth = Math.max(SCOREBOX_MIN_TEXT_WIDTH, textWidth) * boxScale;

  return Math.max(minWidth, calculatedWidth);
}

/**
 * Calculate the position for an outcome label relative to its seat.
 *
 * @param seatWidth - Seat width in pixels
 * @param seatHeight - Seat height in pixels
 * @param scoreboxConfig - Scorebox configuration
 * @param scoreboxWidth - Calculated scorebox width
 * @param labelHeight - Height of the outcome label
 * @returns Local position within seat container
 */
export function calculateOutcomeLabelPosition(
  seatWidth: number,
  seatHeight: number,
  scoreboxConfig: { x: number; y: number },
  scoreboxWidth: number,
  labelHeight: number
): { x: number; y: number } {
  const scoreboxLocalX = -seatWidth / 2 + scoreboxConfig.x;
  const finalX = scoreboxLocalX + scoreboxWidth;
  const scoreboxBottom = seatHeight / 2 + scoreboxConfig.y;

  return {
    x: finalX,
    y: scoreboxBottom - labelHeight,
  };
}

// ===========================================
// CHIP POSITION IN KITTY
// ===========================================

/**
 * Calculate the Y offset for a chip in the kitty stack.
 * Chips stack bottom-up (smallest denomination at bottom).
 *
 * @param denomination - Chip denomination
 * @param existingDenominations - Array of denominations already in kitty
 * @param verticalOffset - Vertical spacing between chips
 * @returns Y offset within kitty container (negative = upward)
 */
export function calculateKittyChipYOffset(
  denomination: number,
  existingDenominations: number[],
  verticalOffset: number = 8
): number {
  // Build sorted list including the new denomination
  const allDenoms = [...new Set([...existingDenominations, denomination])].sort((a, b) => a - b);

  // Find index of this denomination in sorted order
  const index = allDenoms.indexOf(denomination);

  // Position: smallest at bottom (y=0), larger denominations go up (negative Y)
  return -index * verticalOffset;
}

/**
 * Transform a local Y offset in kitty to world coordinates.
 *
 * @param kittyX - Kitty container X position
 * @param kittyY - Kitty container Y position
 * @param localY - Local Y offset within kitty
 * @param rotationRad - Kitty rotation in radians
 * @returns World coordinates
 */
export function kittyLocalToWorld(
  kittyX: number,
  kittyY: number,
  localY: number,
  rotationRad: number
): { x: number; y: number } {
  return {
    x: kittyX + localY * -Math.sin(rotationRad),
    y: kittyY + localY * Math.cos(rotationRad),
  };
}

// ===========================================
// AVATAR POSITION CALCULATIONS
// ===========================================

/**
 * Calculate the avatar position from pre-computed seat dimensions.
 * Avatar is positioned at the bottom-right corner of the seat box,
 * with the center exactly on the corner (border intersects avatar in half).
 *
 * Use this version when you already have seat dimensions calculated.
 * For convenience when you have seatConfig, use calculateAvatarPosition() instead.
 *
 * @param seatX - Seat X position
 * @param seatY - Seat Y position
 * @param seatWidth - Pre-computed seat width
 * @param seatHeight - Pre-computed seat height
 * @param rotationDegrees - Seat rotation in degrees
 * @returns World coordinates for the avatar center
 */
export function calculateAvatarPositionRaw(
  seatX: number,
  seatY: number,
  seatWidth: number,
  seatHeight: number,
  rotationDegrees: number
): { x: number; y: number } {
  // Avatar center is at bottom-right corner of seat
  const localAvatarX = seatWidth / 2;
  const localAvatarY = seatHeight / 2;

  // Transform local offset to world coordinates based on seat rotation
  const rotRad = degreesToRadians(rotationDegrees);
  const cos = Math.cos(rotRad);
  const sin = Math.sin(rotRad);

  return {
    x: seatX + localAvatarX * cos - localAvatarY * sin,
    y: seatY + localAvatarX * sin + localAvatarY * cos,
  };
}

/**
 * Calculate the avatar position for a seat.
 * Avatar is positioned at the bottom-right corner of the seat box,
 * with the center exactly on the corner (border intersects avatar in half).
 *
 * Used for:
 * - Avatar image placement (createSeatContainer)
 * - Chip animations to/from avatar (animateOpponentKittyToAvatar, animateSidebetWin)
 * - Chip animations from avatar (animateOpponentBetToKitty)
 *
 * @param seatData - Seat position data { x, y, rotation }
 * @param seatConfig - Per-seat configuration (for scale)
 * @param seatSizeScale - Global seat size scale (percentage, default 100)
 * @returns World coordinates for the avatar center
 */
export function calculateAvatarPosition(
  seatData: SeatPosition,
  seatConfig: SeatConfig = DEFAULT_SEAT_CONFIG,
  seatSizeScale: number = 100
): { x: number; y: number } {
  // Calculate seat dimensions with scaling
  const { width, height } = calculateSeatDimensions(seatConfig, seatSizeScale);

  return calculateAvatarPositionRaw(seatData.x, seatData.y, width, height, seatData.rotation);
}
